import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Wifi, Router, Wrench, Tv, Phone, Clock, Signal, Gift, Tablet, Camera, Cloud } from 'lucide-react';
import { homePackages, businessFiestaPackages, businessPackages } from '../mock';
import { renderSubtext } from '../utils/renderSubtext';

const PackageCard = ({ pkg, onGetItNow, isBusiness = false, anyOptionalExpanded, registerOptional }) => {
  const [selectedOptional, setSelectedOptional] = React.useState(null);
  const [selectedDevice, setSelectedDevice] = React.useState(null);
  // selectedDeviceAddon now stores the NAME of the selected device addon (string) or null
  const [selectedDeviceAddon, setSelectedDeviceAddon] = React.useState(null);
  const [addonOpen, setAddonOpen] = React.useState(false);
  const [promoOpen, setPromoOpen] = React.useState(true);

  // Normalized device addon list (supports legacy `deviceAddon` single object and new `deviceAddonList`)
  const deviceAddonsList = pkg.deviceAddonList || (pkg.deviceAddon ? [pkg.deviceAddon] : []);
  const selectedDeviceAddonObj = deviceAddonsList.find(d => d.name === selectedDeviceAddon);

  // Preload addon images on component mount so they render instantly when sections expand.
  React.useEffect(() => {
    const urls = new Set();
    if (pkg.deviceAddonList) pkg.deviceAddonList.forEach(d => { if (d.type === 'tv') urls.add('/images/addons/tv.png'); });
    if (pkg.deviceAddon?.type === 'tv') urls.add('/images/addons/tv.png');
    const opts = pkg.optionalList || (pkg.optional ? [pkg.optional] : []);
    opts.forEach(o => { if (o.image) urls.add(o.image); });
    urls.forEach(src => {
      const img = new window.Image();
      img.src = src;
    });
  }, [pkg]);

  // Auto-expand Addon Apps when any LIMITED TIME PROMO is selected.
  // This is a legitimate prop→state sync.
  React.useEffect(() => {
    if (selectedDeviceAddon) {
      setAddonOpen(true);
      setPromoOpen(true);
    }
  }, [selectedDeviceAddon]);

  // When Free 6 Month promo is selected, clear any optional addon (they are mutually exclusive).
  // Intentionally only depends on selectedDeviceAddon — re-running on selectedOptional change
  // would cause an infinite loop.
  React.useEffect(() => {
    if (selectedDeviceAddonObj?.type === 'free-months' && selectedOptional) {
      setSelectedOptional(null);
      if (registerOptional) registerOptional(pkg.id, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceAddon]);

  const iconMap = {
    wifi: Wifi,
    router: Router,
    tools: Wrench,
    tv: Tv,
    phone: Phone,
    clock: Clock,
    signal: Signal,
    gift: Gift,
    camera: Camera,
    cloud: Cloud,
    'phone-alt': Phone
  };

  // Resolve currently selected optional item (supports legacy `optional` and new `optionalList`)
  const optionalItemsList = pkg.optionalList || (pkg.optional ? [pkg.optional] : []);
  const selectedOptItemObj = optionalItemsList.find(o => o.name === selectedOptional);

  // Promo badge logic - when free-months promo selected, replace default badge
  const promoBadgeText = (selectedDeviceAddonObj?.type === 'free-months')
    ? selectedDeviceAddonObj.name.replace(/\n/g, ' ').toUpperCase()
    : (pkg.badge || 'FREE 3 MONTH');
  // Hide FREE badge when a device addon other than free-months is selected (TV/iPad → 36-month) or when optional explicitly changes contract
  const showFreeBadge = pkg.badge && !(selectedDevice || (selectedDeviceAddonObj && selectedDeviceAddonObj.type !== 'free-months') || selectedOptItemObj?.contractOverride);

  // Contract label switches based on selections.
  // Priority: deviceAddon > optional > package default.
  const defaultContractLabel = (pkg.contract || '27-month contract').replace(/-month contract.*/i, '-MONTH CONTRACT').toUpperCase();
  let contractLabelText = defaultContractLabel;
  let contractFooterText = pkg.contract;
  if (selectedDeviceAddonObj?.contractOverride) {
    contractLabelText = selectedDeviceAddonObj.contractOverride.replace(/-month contract.*/i, '-MONTH CONTRACT').toUpperCase();
    contractFooterText = selectedDeviceAddonObj.contractOverride;
  } else if (selectedOptItemObj?.contractOverride) {
    contractLabelText = selectedOptItemObj.contractOverride.replace(/-month contract.*/i, '-MONTH CONTRACT').toUpperCase();
    contractFooterText = selectedOptItemObj.contractOverride;
  }
  // Always show the contract badge (it just changes text when overridden)
  const showContractBadge = !!pkg.contract;

  return (
    <div className="relative h-full">
      {pkg.bestSeller && (
        <div
          aria-label="Best Seller"
          style={{
            position: 'absolute',
            top: '-26px',
            left: 0,
            right: 0,
            height: '40px',
            background: 'linear-gradient(135deg, #FF6600 0%, #FF8A3D 100%)',
            color: '#fff',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 800,
            fontSize: '12px',
            letterSpacing: '0.16em',
            borderRadius: '12px 12px 0 0',
            boxShadow: '0 -3px 10px rgba(255,102,0,0.25)',
            zIndex: 0,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: '6px',
          }}
        >
          ★ BEST SELLER
        </div>
      )}
    <Card className="p-6 hover:shadow-2xl transition-all duration-300 border-2 hover:border-[#FF6600] overflow-hidden group flex flex-col h-full" style={{ fontFamily: 'Inter, sans-serif', position: 'relative', zIndex: 1, background: '#fff' }}>
      {/* Speed Header - Fixed height section. When addon apps is hidden, shrink reserved space so content below moves up. */}
      {(() => {
        const hasAnyAddon = deviceAddonsList.length > 0 || pkg.optional || pkg.optionalList;
        const addonAppsHidden = selectedDeviceAddonObj?.type === 'free-months' || (pkg.hideAddonsByDefault && !selectedDeviceAddonObj);
        const headerMinHeight = hasAnyAddon ? (addonAppsHidden ? '90px' : '170px') : 'auto';
        const headerMb = hasAnyAddon ? 'mb-6' : 'mb-2';
        return (
      <div className={`flex-shrink-0 ${headerMb}`} style={{ minHeight: headerMinHeight }}>
        <div style={{ minHeight: '28px', marginBottom: '12px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          {showFreeBadge && (
            <Badge
              className="text-white inline-block"
              style={{
                backgroundColor: '#FF6600',
                padding: '4px 10px',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.04em',
                borderRadius: '6px',
                lineHeight: 1,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {promoBadgeText}
            </Badge>
          )}
          {showContractBadge && (
            <Badge
              className="text-white inline-block"
              style={{
                backgroundColor: '#1701E4',
                padding: '4px 10px',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.04em',
                borderRadius: '6px',
                lineHeight: 1,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {contractLabelText}
            </Badge>
          )}
        </div>
        <h3 style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '30px', color: '#111', marginBottom: '21px', lineHeight: 1 }}>{pkg.speed}</h3>

        {/* LIMITED TIME PROMO - Add-on Device(s) */}
        {deviceAddonsList.length > 0 && (
          <div className="mt-3" style={{ border: '1.5px solid #FFD6B0', borderRadius: '12px', padding: '14px', background: '#FFF7F0' }}>
            <div
              className="flex items-center gap-2 cursor-pointer select-none"
              onClick={() => setPromoOpen(prev => !prev)}
              style={{ marginBottom: promoOpen ? '12px' : 0 }}
            >
              <span
                aria-label={promoOpen ? 'collapse' : 'expand'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '16px',
                  height: '16px',
                  border: '1.5px solid #FFB380',
                  borderRadius: '3px',
                  fontSize: '12px',
                  color: '#FF6600',
                  lineHeight: 1,
                  fontWeight: 700,
                }}
              >
                {promoOpen ? '−' : '+'}
              </span>
              <span style={{ fontSize: '14px' }}>🔥</span>
              <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '11px', color: '#FF6600', letterSpacing: '0.08em', textTransform: 'uppercase', margin: 0, flexGrow: 1 }}>Limited Time Promo</p>
            </div>
            <div className="space-y-2" style={{ display: promoOpen ? 'block' : 'none' }}>
              {deviceAddonsList.map((addon) => {
                const isSelected = selectedDeviceAddon === addon.name;
                return (
                  <div
                    key={addon.name}
                    onClick={() => setSelectedDeviceAddon(isSelected ? null : addon.name)}
                    className={`rounded-lg border cursor-pointer transition-all ${
                      isSelected ? 'border-[#FF6600] bg-orange-50' : 'border-orange-200 bg-white hover:border-orange-300'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent', outline: 'none', userSelect: 'none', padding: '10px' }}
                  >
                    <div className="flex items-center gap-2" style={{ minHeight: '50px' }}>
                      {isSelected ? (
                        <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0"></div>
                      )}
                      {addon.type === 'tv' ? (
                        <img
                          src="/images/addons/tv.png"
                          alt={addon.name}
                          className="h-9 w-9 object-contain flex-shrink-0"
                        />
                      ) : addon.type === 'ipad' ? (
                        <Tablet className="h-9 w-9 text-[#FF6600] flex-shrink-0" strokeWidth={1.5} />
                      ) : addon.type === 'camera' ? (
                        <Camera className="h-9 w-9 text-[#FF6600] flex-shrink-0" strokeWidth={1.5} />
                      ) : (
                        <Gift className="h-9 w-9 text-[#FF6600] flex-shrink-0" strokeWidth={1.5} />
                      )}
                      <div className="flex-grow min-w-0">
                        <p className="text-sm font-semibold text-gray-900 leading-tight" style={{ whiteSpace: 'pre-line' }}>{addon.name}</p>
                        <p style={{ color: '#888', fontWeight: 500, fontSize: '11px', marginTop: '2px', whiteSpace: 'nowrap' }}>
                          {addon.contract}
                        </p>
                      </div>
                      {/* Right-side price label — hidden for addons that use priceOverride,
                          since the price is already baked into the addon name (e.g. "RM59\nFirst 10 Month..."). */}
                      {!addon.priceOverride && (
                        <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '12px', color: '#FF6600', flexShrink: 0, whiteSpace: 'nowrap' }}>
                          {addon.priceLabel ? addon.priceLabel : `+RM${addon.price}/mth`}
                        </span>
                      )}
                    </div>
                    {isSelected && addon.note && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#666', lineHeight: 1.5 }}>
                          📦 {addon.note}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Netflix Optional - Single option with scrollable dropdown */}
        {(pkg.optional || pkg.optionalList) && !pkg.deviceOptions && !(selectedDeviceAddonObj?.type === 'free-months') && !(pkg.hideAddonsByDefault && !selectedDeviceAddonObj) && (() => {
          const items = pkg.optionalList || (pkg.optional ? [pkg.optional] : []);
          return (
          <div className="mt-3" style={{ border: '1.5px solid #e5e5e5', borderRadius: '12px', padding: '14px' }}>
            <div
              className="flex items-center gap-2 cursor-pointer select-none"
              onClick={() => setAddonOpen(prev => !prev)}
              style={{ marginBottom: addonOpen ? '12px' : 0 }}
            >
              <span
                aria-label={addonOpen ? 'collapse' : 'expand'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '16px',
                  height: '16px',
                  border: '1.5px solid #ccc',
                  borderRadius: '3px',
                  fontSize: '12px',
                  color: '#aaa',
                  lineHeight: 1,
                  fontWeight: 700,
                }}
              >
                {addonOpen ? '−' : '+'}
              </span>
              <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '11px', color: '#999', letterSpacing: '0.08em', textTransform: 'uppercase', margin: 0, flexGrow: 1 }}>Addon Apps (Optional)</p>
            </div>
            <div className="space-y-2" style={{ display: addonOpen ? 'block' : 'none' }}>
              {items.map((item, itemIdx) => {
                const isSelected = selectedOptional === item.name;
                return (
                  <div
                    key={item.name}
                    onClick={() => {
                      const newVal = isSelected ? null : item.name;
                      setSelectedOptional(newVal);
                      if (registerOptional) registerOptional(pkg.id, newVal !== null);
                    }}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected ? 'border-[#FF6600] bg-orange-50' : 'border-gray-200 hover:border-orange-300'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent', outline: 'none', userSelect: 'none' }}
                  >
                    <div className="flex items-center gap-2">
                      {isSelected ? (
                        <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0"></div>
                      )}
                      <img
                        src={item.image || '/images/addons/netflix.png'}
                        alt={item.name}
                        className="h-10 w-10 object-contain"
                      />
                      <div className="flex-grow min-w-0">
                        <p className="text-sm font-semibold text-gray-900 leading-tight">{item.name}</p>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <ul className="space-y-2 text-sm text-gray-700">
                          {item.details.map((detail, idx) => (
                            <li key={`${item.name}-${idx}`} className="flex items-start gap-2">
                              <svg className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                          <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '14px', color: '#FF6600' }}>
                            +RM{item.price}/mth
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          );
        })()}

        {/* Device Options - Two options for TV Pack bundles with scrollable dropdown */}
        {pkg.deviceOptions && (
          <div className="mt-3" style={{ border: '1.5px solid #e5e5e5', borderRadius: '12px', padding: '14px', minHeight: '220px' }}>
            <div className="flex items-center gap-2 mb-3">
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '16px', height: '16px', border: '1.5px solid #ccc', borderRadius: '3px', fontSize: '12px', color: '#aaa', lineHeight: 1 }}>+</span>
              <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '11px', color: '#999', letterSpacing: '0.08em', textTransform: 'uppercase', margin: 0 }}>Addon Apps (Optional)</p>
            </div>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {pkg.deviceOptions.map((device) => (
                <div 
                  key={device.name}
                  onClick={() => setSelectedDevice(selectedDevice === device.name ? null : device.name)}
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedDevice === device.name ? 'border-[#FF6600] bg-orange-50' : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  {selectedDevice === device.name ? (
                    <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0"></div>
                  )}
                  <img 
                    src={device.image}
                    alt={device.name}
                    className="h-8"
                  />
                  <div className="flex-grow">
                    <p className="text-sm font-semibold">{device.name}</p>
                    <p className="text-xs text-gray-500">+RM{device.price}/month</p>
                  </div>
                  <span className="text-lg text-gray-400 font-bold">+</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
        );
      })()}

      {/* Recommended */}
      <div className="mb-4 flex-shrink-0" style={{ minHeight: '64px' }}>
        {pkg.recommended && (
          <>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 400, color: '#444', lineHeight: 1.5, marginBottom: '4px' }}>{pkg.recommended}</p>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 400, color: '#888', lineHeight: 1.5 }}>{pkg.users}</p>
          </>
        )}
      </div>

      {/* Divider */}
      <div className="border-t my-6 flex-shrink-0"></div>

      {/* What's Included — extras from selected device addon (e.g., AI Camera promo) are appended */}
      <div className="mb-10" style={{ minHeight: '200px' }}>
        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 700, color: '#111', marginBottom: '16px' }}>What&apos;s included</p>
        <div className="space-y-3">
          {[
            ...pkg.includes,
            ...(selectedDeviceAddonObj?.includesAdd || [])
          ].map((item, index) => {
            const Icon = iconMap[item.icon];
            return (
              <div key={`${item.icon}-${index}`} className="flex items-start gap-3">
                {Icon && <Icon className="w-5 h-5 text-[#FF6600] flex-shrink-0 mt-0.5" />}
                <div>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 600, color: '#111', lineHeight: 1.4 }}>{item.text}</p>
                  {item.subtext && <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 400, color: '#666', lineHeight: 1.4 }}>{renderSubtext(item.subtext)}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Price */}
      <div className="mb-6 flex-shrink-0 mt-auto" style={{ minHeight: '72px' }}>
        {(() => {
          // If the currently selected device addon has priceOverride, render that instead
          // of the computed base + optional total. This is used by time-limited promos
          // that replace the standard pricing display (e.g. "RM59 for first 10 months").
          if (selectedDeviceAddonObj?.priceOverride) {
            const po = selectedDeviceAddonObj.priceOverride;
            return (
              <>
                <div className="flex items-baseline gap-2">
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '32px', fontWeight: 800, color: '#FF6600', lineHeight: 1 }}>{po.amount}</span>
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#666' }}>{po.period}</span>
                </div>
                {po.strikeThrough && (
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#aaa', marginTop: '4px', textDecoration: 'line-through', minHeight: '16px' }}>
                    {po.strikeThrough}
                  </p>
                )}
              </>
            );
          }
          const basePrice = parseFloat((pkg.price || '').replace(/[^\d.]/g, '')) || 0;
          const optionalItems = pkg.optionalList || (pkg.optional ? [pkg.optional] : []);
          const selectedOptItem = optionalItems.find(o => o.name === selectedOptional);
          const optionalPrice = selectedOptItem ? selectedOptItem.price : 0;
          const deviceAddonPrice = selectedDeviceAddonObj ? (selectedDeviceAddonObj.price || 0) : 0;
          const total = basePrice + optionalPrice + deviceAddonPrice;
          const extras = [];
          if (selectedOptItem) extras.push(`RM${selectedOptItem.price} (${selectedOptItem.name})`);
          if (selectedDeviceAddonObj && selectedDeviceAddonObj.price > 0) extras.push(`RM${selectedDeviceAddonObj.price} (${selectedDeviceAddonObj.name.replace(/\n/g, ' ')})`);
          const hasExtra = extras.length > 0;
          return (
            <>
              <div className="flex items-baseline gap-2">
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '32px', fontWeight: 800, color: '#FF6600', lineHeight: 1 }}>RM{total.toFixed(2)}</span>
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#666' }}>/month</span>
              </div>
              {hasExtra ? (
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#888', marginTop: '4px' }}>
                  {pkg.price} + {extras.join(' + ')}
                </p>
              ) : (
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#aaa', marginTop: '4px', textDecoration: pkg.originalPrice ? 'line-through' : 'none', minHeight: '16px' }}>
                  {pkg.originalPrice || '\u00A0'}
                </p>
              )}
            </>
          );
        })()}
      </div>

      {/* CTA Button */}
      <Button 
        onClick={() => onGetItNow({ ...pkg, selectedOptional, selectedDevice, selectedDeviceAddon })}
        className="w-full bg-[#FF6600] hover:bg-[#E55A00] text-white rounded-lg transition-all flex-shrink-0"
        style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '15px', paddingTop: '14px', paddingBottom: '14px', height: 'auto' }}
      >
        Get It Now
      </Button>
      {contractFooterText && (
        <p className="text-center text-xs text-gray-500 mt-2" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
          {contractFooterText}
        </p>
      )}
    </Card>
    </div>
  );
};

const BusinessPackageCard = ({ pkg, onGetItNow }) => {
  const iconMap = {
    wifi: Wifi,
    router: Router,
    tools: Wrench,
    tv: Tv,
    phone: Phone,
    clock: Clock,
    signal: Signal,
    'phone-alt': Phone,
  };

  return (
    <Card className="p-6 hover:shadow-2xl transition-all duration-300 border-2 hover:border-[#FF6600] relative overflow-hidden group flex flex-col h-full">
      {pkg.badge && (
        <div className="absolute top-4 right-4 z-10">
          <Badge className="bg-[#FF6600] text-white px-3 py-1 text-xs">
            {pkg.badge}
          </Badge>
        </div>
      )}

      <div className="mb-6 flex-shrink-0">
        <h3 className="text-3xl font-bold text-gray-900 mb-2">{pkg.speed}</h3>
        {pkg.device && (
          <p className="text-sm text-gray-600 mt-1">+ {pkg.device}</p>
        )}
      </div>

      <div className="border-t my-6 flex-shrink-0"></div>

      <div className="mb-6 flex-grow">
        <p className="text-sm font-bold text-gray-900 mb-4">What&apos;s included</p>
        <div className="space-y-3">
          {pkg.includes.map((item, index) => {
            const Icon = iconMap[item.icon];
            return (
              <div key={`${item.icon}-${index}`} className="flex items-start gap-3">
                <Icon className="w-5 h-5 text-[#FF6600] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">{item.text}</p>
                  {item.subtext && <p className="text-sm text-gray-600">{item.subtext}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mb-6 flex-shrink-0 mt-auto">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-bold text-[#FF6600]">{pkg.price}</span>
          <span className="text-gray-600">/month</span>
        </div>
      </div>

      <Button
        onClick={() => onGetItNow(pkg)}
        className="w-full bg-[#FF6600] hover:bg-[#E55A00] text-white py-6 text-lg font-semibold rounded-lg transition-all flex-shrink-0"
      >
        Get It Now
      </Button>

      <p className="text-center text-sm text-gray-500 mt-4 flex-shrink-0">{pkg.contract}</p>
    </Card>
  );
};

const PackagesSection = ({ onGetItNow, activeTab: activeTabProp, setActiveTab: setActiveTabProp }) => {
  const [activeTabLocal, setActiveTabLocal] = useState('home');
  const activeTab = activeTabProp !== undefined ? activeTabProp : activeTabLocal;
  const setActiveTab = setActiveTabProp || setActiveTabLocal;
  const [anyOptionalExpanded, setAnyOptionalExpanded] = useState(false);
  const expandedMapRef = React.useRef(new Set());
  const registerOptional = React.useCallback((id, isExpanded) => {
    if (isExpanded) {
      expandedMapRef.current.add(id);
    } else {
      expandedMapRef.current.delete(id);
    }
    setAnyOptionalExpanded(expandedMapRef.current.size > 0);
  }, []);

  return (
    <div className="bg-white py-20" id="package">
      <div className="container mx-auto px-4">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-20 h-12">
            <TabsTrigger value="home" className="text-lg">Home Plans</TabsTrigger>
            <TabsTrigger value="business" className="text-lg">Business Plans</TabsTrigger>
          </TabsList>

          {/* Home Packages */}
          <TabsContent value="home" className="space-y-16">
            {/* Section title — fixed height keeps package columns aligned across tabs */}
            <div className="text-center -mt-10 mb-4 max-w-3xl mx-auto px-4" style={{ minHeight: '170px' }}>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4 tracking-tight">
                CUSTOMISE YOUR <span className="text-[#FF6600]">UNIFI HOME</span> BROADBAND PLAN
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                Create a broadband internet plan that fits your daily needs. Enjoy lightning-fast fibre broadband with seamless connectivity for streaming, gaming, and working from home. Choose from the best broadband plans in Malaysia for reliable, high-speed broadband across all your devices.
              </p>
            </div>
            {/* Regular Home Packages */}
            <div className="grid grid-cols-3 gap-x-6 gap-y-[38px] items-stretch" style={{ maxWidth: '1100px', margin: '0 auto' }}>
              {homePackages.map((pkg) => (
                <PackageCard 
                  key={pkg.id} 
                  pkg={pkg} 
                  onGetItNow={onGetItNow}
                  anyOptionalExpanded={anyOptionalExpanded}
                  registerOptional={registerOptional}
                />
              ))}
            </div>
          </TabsContent>

          {/* Business Packages */}
          <TabsContent value="business" className="space-y-16">
            {/* Section title — fixed height keeps package columns aligned across tabs */}
            <div className="text-center -mt-10 mb-4 max-w-3xl mx-auto px-4" style={{ minHeight: '170px' }}>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4 tracking-tight">
                CUSTOMISE YOUR <span className="text-[#FF6600]">UNIFI BUSINESS</span> BROADBAND
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                Reliable, high-speed fibre broadband built for businesses. From small offices to large workplaces, choose the plan that keeps your team connected and productive every day.
              </p>
            </div>
            {/* Business Packages — same layout as Home Plans */}
            <div className="grid grid-cols-3 gap-x-6 gap-y-[38px] items-stretch" style={{ maxWidth: '1100px', margin: '0 auto' }}>
              {businessPackages.map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  pkg={pkg}
                  onGetItNow={onGetItNow}
                  isBusiness={true}
                  anyOptionalExpanded={anyOptionalExpanded}
                  registerOptional={registerOptional}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Disclaimer */}
        <div className="mt-12 text-sm text-gray-600" style={{ textAlign: 'left' }}>
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px', lineHeight: 1.7 }}>
            <li>Price displayed is EXCLUSIVE of SST.</li>
            <li>Every package has a minimum subscription, depending on which one you choose.</li>
            <li>Free installation only covers standard installation cabling (currently no charges) up to 50 meters of internal fiber. This excludes charges imposed by external contractors for concealed wiring, wiring over the ceiling, customized wiring, etc</li>
            {activeTab === 'business' ? (
              <li>Company New registration Unifi require to pay <strong>RM100 advance payment</strong> within 10 days after installation complete.</li>
            ) : (
              <>
                <li>Malaysian New registration Unifi require to pay <strong>RM100 advance payment</strong> within 10 days after installation complete.</li>
                <li>Foreigner New registration Unifi require to pay <strong>RM500 deposit payment</strong> before installation complete.</li>
              </>
            )}
            <li>For <strong>2Gbps Plan</strong> speed is based on aggregated offering.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PackagesSection;
